import {
    put,
    race,
    SagaIterator,
    select,
    takeErrorRequest,
    takeFatalErrorRequest,
    takeLatest,
    takeSuccessfulRequest
} from './core/sagaEffects';
import union from 'lodash.union';
import { AirportsRequest } from 'acscp/api/requests';
import { ClearAirportsSearchResults, FetchRequest, FetchSuccess, ReduxAction, SearchAirports, SearchAirportsComplete } from 'acscp/actions';
import { Airport, AppState, DateTime } from 'acscp/store';
import { AirportsList } from 'acscp/api/models/airportsList';
import log from 'loglevel';

export function* airportSearchSaga(): SagaIterator {
     yield takeLatest(SearchAirports, searchAirports);
}

interface SearchResults {
    airports: Airport[];
    updated: DateTime;
}

export function searchResultsSelector(state: AppState, query: string): SearchResults | null {
    const existingResults = state.airports.searchResults[query];

    if (!existingResults) {
        return null;
    }

    return {
        airports: existingResults.airports.map((id: string) => {
            return state.airports.airports[id];
        }),
        updated: existingResults.lastUpdated
    };
    }

const RESULT_CACHE_EXPIRY_MILLIS = 15 * 60 * 1000;

export function* searchAirports(action: SearchAirports): SagaIterator {
    const queryString = action.query.trim();

    if (queryString.length < 3) {
        yield put(new ClearAirportsSearchResults());
        return;
    }

    const existingResults = (yield select(searchResultsSelector, queryString.substring(0, 3))) as unknown as SearchResults | null;
    if (existingResults) {
        const age = DateTime.Now().diff(existingResults.updated);

        const previouslySearchedResults = filterList(queryString, existingResults.airports);
        yield put(new SearchAirportsComplete(previouslySearchedResults));

        if (age > RESULT_CACHE_EXPIRY_MILLIS) {
            log.info('Existing results are too old: ', existingResults);
            yield *performSearchRequest(queryString);
        }
    } else {
        yield *performSearchRequest(queryString);
    }
}

function* performSearchRequest(queryString: string): SagaIterator {
    yield put(new FetchRequest(new AirportsRequest(queryString.substring(0, 3))));

    const {successAction} = (yield race({
        successAction: takeSuccessfulRequest(AirportsRequest),
        error: takeErrorRequest(AirportsRequest),
        failedResponse: takeFatalErrorRequest(AirportsRequest)
    })) as unknown as {successAction: ReduxAction<FetchSuccess<AirportsList, AirportsRequest>>};

    if (successAction) {
        const response = successAction.payload.response;
        const searchResults = filterList(queryString, response);
        yield put(new SearchAirportsComplete(searchResults));
    }
}

function filterList(searchString: string, airports: Airport[]): Airport[] {
    if (searchString === null || searchString.length === 0) {
        return [];
    }

    const alphabetisedAirports = airports.sort((x, y) => x.name.localeCompare(y.name));
    const lowercaseInput = searchString.toLowerCase();

    const iataMatches = alphabetisedAirports.filter((airport: Airport) =>
        !!airport.iataCode && airport.iataCode.toLowerCase().indexOf(lowercaseInput) !== -1
    );

    const icaoMatches = alphabetisedAirports.filter((airport: Airport) =>
        !!airport.icaoCode && airport.icaoCode.toLowerCase().indexOf(lowercaseInput) !== -1
    );

    const startsWithNameMatches = alphabetisedAirports.filter((airport: Airport) =>
        !!airport.name && airport.name.toLowerCase().indexOf(lowercaseInput) === 0
    );

    const nameMatches = alphabetisedAirports.filter((airport: Airport) =>
        !!airport.name && airport.name.toLowerCase().indexOf(lowercaseInput) !== -1
    );

    const townMatches = alphabetisedAirports.filter((airport: Airport) =>
        !!airport.town && airport.town.toLowerCase().indexOf(lowercaseInput) !== -1
    );

    return union(iataMatches, icaoMatches, startsWithNameMatches, nameMatches, townMatches).slice(0, 20);
}
